import type { HelpMap } from './types';

/** Parametry świata (suwaki w „Środowisku”) i cechy w genomie. Klucze: `param.<nazwa>` oraz `trait.<nazwa>`. */
export const PARAMS: HelpMap = {
  // ——— grupy ———
  'group.Zasoby': {
    group: 'Symulacja',
    title: 'Grupa „Zasoby”',
    short: 'Jak szybko odrasta pokarm, ile go maksymalnie jest i jak wygląda krajobraz żyzności.',
    more: ['To najsilniejsze pokrętła ekosystemu: od ilości energii wpływającej do świata zależy, ile organizmów da się wyżywić i jaka strategia się opłaca.'],
    glossary: false,
  },
  'group.Koszty': {
    group: 'Symulacja',
    title: 'Grupa „Koszty”',
    short: 'Ile energii kosztuje życie, ruch, zmysły, mózg i wysiłek jedzenia lub ataku.',
    more: ['Zwiększ koszt czegoś, a ewolucja sama „oszczędza” to, co nie jest niezbędne. Sprawdź np. koszt sensorów: gdy zbyt wysoki, organizmy stają się ślepe.'],
    glossary: false,
  },
  'group.Życie': {
    group: 'Symulacja',
    title: 'Grupa „Życie”',
    short: 'Długość życia, śmiertelność losowa, koszt rozrodu i limit populacji.',
    glossary: false,
  },
  'group.Drapieżnictwo': {
    group: 'Symulacja',
    title: 'Grupa „Drapieżnictwo”',
    short: 'Siła ataku i jego koszt. Zerowa siła wyłącza drapieżnictwo całkowicie.',
    glossary: false,
  },
  'group.Ewolucja': {
    group: 'Symulacja',
    title: 'Grupa „Ewolucja”',
    short: 'Tempo i siła mutacji oraz sposób rozmnażania (bezpłciowe lub płciowe).',
    glossary: false,
  },
  'group.Klimat': {
    group: 'Symulacja',
    title: 'Grupa „Klimat”',
    short: 'Temperatura bazowa, wpływ zimna i pory roku.',
    glossary: false,
  },

  // ——— zasoby ———
  'param.regrowthRate': {
    group: 'Świat i środowisko',
    title: 'Tempo wzrostu zasobów',
    short: 'Względny przyrost pokarmu na tick. Większe = więcej energii wpływa do świata = więcej organizmów.',
    more: ['Wzrost jest logistyczny, proporcjonalny do istniejącej biomasy: komórka wyjedzona do zera odrasta wolno. Zbyt niskie tempo daje cykle „przejadania” i odbudowy oraz małe populacje, zbyt wysokie — tłok i konkurencję kontaktową.'],
    try: 'Ustaw 0,02 i obserwuj cykle: duże ciała i oscylacje liczebności. Ustaw 0,2 i sprawdź, czy populacja nadal rośnie, czy już ją hamuje drapieżnik.',
    fun: 'Przy niskich wartościach w testach autora częściej wygrywały większe ciała; przy wysokich — małe.',
    see: ['logistic', 'carrying', 'ledger'],
  },
  'param.foodSeed': {
    group: 'Świat i środowisko',
    title: 'Rozsiew („nasiona”)',
    short: 'Ułamek pojemności, który działa jak zalążek odrostu, gdy w komórce zostało prawie zero pokarmu.',
    more: ['Bez nasion komórka wyjedzona do zera nigdy by się nie odbudowała (wzrost ∝ biomasa). Większy rozsiew skraca „czas dojścia do siebie” po przejedzeniu.'],
    see: ['logistic'],
  },
  'param.foodCapMax': {
    group: 'Świat i środowisko',
    title: 'Pojemność komórki',
    short: 'Maksymalna ilość pokarmu w najżyźniejszej komórce. Mniej żyzne komórki mają proporcjonalnie mniej.',
    see: ['fertility', 'resources'],
  },
  'param.foodDecay': {
    group: 'Świat i środowisko',
    title: 'Rozkład nadmiaru',
    short: 'Jak szybko pokarm ponad pojemność komórki (np. po dosypaniu lub od zwłok) psuje się i zamienia w ciepło.',
    more: ['Zanik jest księgowany jako strata energii, więc bilans nadal się zgadza.'],
    see: ['ledger', 'addfood'],
  },
  'param.patchDrift': {
    group: 'Świat i środowisko',
    title: 'Dryf plam żyzności',
    short: 'Z jaką prędkością żyzne plamy wędrują po świecie (jednostek na tick). 0 = stoją w miejscu.',
    more: ['Plamy zmieniają kierunek losowo i odbijają się od ścian. Gdy dryf jest wolniejszy niż organizmy, można podążać za plamą; gdy szybszy — trzeba ją umieć „znaleźć”.'],
    try: 'Ustaw 0,3 i włącz chip „Zasoby”, żeby zobaczyć wędrówkę plam.',
    see: ['patches', 'memory'],
  },
  'param.patchLife': {
    group: 'Świat i środowisko',
    title: 'Cykl życia plam',
    short: 'Po ilu tickach plama wygasa i odradza się w innym miejscu. 0 = trwałe.',
    more: ['Plama najpierw rośnie, potem słabnie i znika, a po odrodzeniu pojawia się w losowym miejscu z losową siłą. Fazy są rozłożone, więc plamy nie wygasają jednocześnie.'],
    see: ['patches'],
  },
  'param.contestPower': {
    group: 'Świat i środowisko',
    title: 'Przewaga rozmiaru w rywalizacji',
    short: 'Jak mocno większe organizmy wygrywają w walce o ten sam pokarm: udział ∝ (promień/4)^k, k = 0…3.',
    more: ['k = 0 oznacza podział wyłącznie wg apetytu. Wyższe k premiuje duże ciała, ale nie niweluje ich kosztów życia.'],
    see: ['competition', 'allometry'],
  },

  // ——— koszty ———
  'param.basalCoef': {
    group: 'Organizm',
    title: 'Metabolizm podstawowy',
    short: 'Ile energii na tick kosztuje samo bycie żywym: współczynnik · masa^0,75.',
    more: ['To „czynsz” za ciało. Podniesienie go skraca czas, jaki organizm przetrwa bez jedzenia, i podnosi próg przeżycia — wygrywają mniejsze, oszczędniejsze organizmy.'],
    see: ['kleiber', 'metabolism'],
  },
  'param.moveCoef': {
    group: 'Organizm',
    title: 'Koszt ruchu',
    short: 'Koszt ruchu na tick: współczynnik · masa^0,75 · prędkość². Prędkość podwojona to czterokrotny koszt.',
    try: 'Zwiększ koszt i obserwuj, czy średnia „Prędkość maks.” maleje — albo czy sieć po prostu rzadziej się rusza.',
    see: ['trait.speed', 'metabolism'],
  },
  'param.turnCoef': {
    group: 'Organizm',
    title: 'Koszt skręcania',
    short: 'Koszt zmiany kierunku, proporcjonalny do wielkości skrętu.',
    more: ['Zniechęca do „kręcenia się w kółko”.'],
    see: ['metabolism'],
  },
  'param.senseCoef': {
    group: 'Organizm',
    title: 'Koszt sensorów',
    short: 'Koszt zasięgu zmysłów: współczynnik · zasięg². Widzieć dwa razy dalej kosztuje cztery razy więcej.',
    more: ['W testach autora domyślny koszt jest tak mały, że nie zmieniał ewoluowanego zasięgu w porównaniu z zerem. Przy 50× większym koszcie zasięg spadał z ok. 0,36 do 0,02 — organizmy prawie oślepły, a razem z nimi wymarli drapieżnicy.'],
    try: 'Ustaw 0,0003 w jednym ze światów-bliźniaków i porównaj „Zasięg sensorów” oraz udział drapieżników.',
    see: ['trait.sense', 'tradeoff'],
  },
  'param.brainCoef': {
    group: 'Organizm',
    title: 'Koszt mózgu',
    short: 'Koszt utrzymania sieci: współczynnik · suma wartości bezwzględnych wag. Większe połączenia kosztują więcej.',
    more: ['Dzięki temu „nieużywane” wagi opłaca się wyzerować, a silne połączenia muszą się zwracać. To odpowiednik realnych kosztów tkanki nerwowej.'],
    fun: 'Ludzki mózg to ok. 2% masy ciała, a zużywa ok. 20% energii spoczynkowej.',
    see: ['brain', 'tradeoff'],
  },
  'param.eatCoef': {
    group: 'Organizm',
    title: 'Koszt żerowania',
    short: 'Koszt wysiłku jedzenia: współczynnik · wysiłek · kęs. Płacony nawet wtedy, gdy nic nie ma do jedzenia.',
    more: ['Dzięki temu sieci opłaca się „włączać” jedzenie tylko tam, gdzie jest pokarm.'],
    see: ['outputs', 'trait.bite'],
  },
  'param.coldUpkeepCoef': {
    group: 'Organizm',
    title: 'Koszt odporności na zimno',
    short: 'Stały koszt utrzymania izolacji: współczynnik · gen odporności · masa^0,75, płacony także w cieple.',
    more: ['To powód, dla którego w ciepłym świecie odporność na zimno zwykle znika, a w zimnym — rośnie.'],
    see: ['trait.coldTolerance', 'temperature'],
  },

  // ——— życie ———
  'param.reproOverhead': {
    group: 'Organizm',
    title: 'Narzut reprodukcji',
    short: 'Jaka część energii przekazanej potomkowi „marnuje się” jako ciepło przy rozmnażaniu.',
    more: ['Wyższy narzut uczyni rozmnażanie droższym i poprzesuwa selekcję ku wolniejszym, lepiej wyposażonym potomkom.'],
    see: ['reproduction'],
  },
  'param.accidentRate': {
    group: 'Organizm',
    title: 'Śmiertelność losowa',
    short: 'Prawdopodobieństwo śmierci „z byle powodu” w danym ticku, niezależnie od energii czy wieku.',
    more: ['Losowa śmiertelność premiuje wczesne rozmnażanie: jeśli możesz zginąć w każdej chwili, warto mieć potomstwo jak najszybciej.'],
    see: ['death', 'trait.maturity'],
  },
  'param.lifespanBase': {
    group: 'Organizm',
    title: 'Bazowa długość życia',
    short: 'Maksymalny wiek w tickach dla masy 1; faktyczny limit to to · masa^0,25.',
    see: ['age', 'senescence'],
  },
  'param.maxPopulation': {
    group: 'Świat i środowisko',
    title: 'Limit populacji',
    short: 'Twardy bezpiecznik: po jego przekroczeniu organizmy przestają się rozmnażać. Normalnie populację ogranicza pokarm, nie ten limit.',
    see: ['carrying'],
  },
  'param.corpseRecycling': {
    group: 'Świat i środowisko',
    title: 'Recykling zwłok',
    short: 'Jaka część energii zmarłego wraca do gleby jako pokarm (reszta znika jako ciepło).',
    more: ['Wyższy recykling zamyka obieg energii i pozwala drapieżnikom oraz padlinożernym strategiom działać na „pozostałościach”.'],
    see: ['corpse'],
  },

  // ——— drapieżnictwo ———
  'param.predationPower': {
    group: 'Ekologia',
    title: 'Siła ataku drapieżników',
    short: 'Ile energii na tick odbiera ofierze atakujący o diecie 1 i masie 1. 0 wyłącza drapieżnictwo.',
    more: ['W testach autora słabszy atak (2) powodował wymieranie drapieżników i zanik zdolności ataku, a mocniejszy (4–6) dawał trwałe współistnienie z roślinożercami. To tak, jakby przy zbyt słabych szponach nie opłacało się polować.'],
    try: 'Wybierz scenariusz „Arena drapieżników” lub porównaj wartości 0 i 4 w Teście A/B.',
    see: ['predation', 'trophic', 'ab'],
  },
  'param.attackCoef': {
    group: 'Ekologia',
    title: 'Koszt ataku',
    short: 'Koszt wysiłku ataku na tick: współczynnik · wysiłek · masa^0,75 — płacony tylko przez organizmy z dietą powyżej progu.',
    more: ['Dzięki temu organizm ma powód, by atakować tylko wtedy, gdy ofiara jest w zasięgu.'],
    see: ['predation', 'outputs'],
  },
  'param.sexual': {
    group: 'Organizm',
    title: 'Rozmnażanie płciowe (0/1)',
    short: '0 = bezpłciowe (klon + mutacje). 1 = płciowe: dwoje rodziców, rekombinacja neuron po neuronie.',
    more: ['Można przełączyć w trakcie eksperymentu. Zmiana działa od następnego ticku, a poprzedni tryb zostaje w dzienniku zmian parametrów.'],
    see: ['sexual', 'crossover', 'hitchhiking'],
  },
  'param.mateRange': {
    group: 'Organizm',
    title: 'Zasięg szukania partnera',
    short: 'Odległość (ponad sumę promieni), w której organizm w trybie płciowym może znaleźć gotowego partnera.',
    more: ['Za mały zasięg w rzadkiej populacji oznacza, że organizmy nie spotykają partnerów i się nie rozmnażają.'],
    see: ['sexual'],
  },

  // ——— ewolucja ———
  'param.mutationRate': {
    group: 'Ewolucja',
    title: 'Częstość mutacji',
    short: 'Szansa, że dany gen zmieni się przy narodzinach. Domyślnie 4% (ok. 9 zmian na noworodka).',
    more: ['Za niska — brakuje zmienności i ewolucja zwalnia. Za wysoka — potomek niszczy to, co odziedziczył, i populacja traci dotychczasowe osiągnięcia (tzw. katastrofa błędów).'],
    try: 'Utwórz bliźniaka i ustaw 0 w jednym świecie. Porównaj różnorodność i liczbę kladów.',
    see: ['mutation', 'diversity', 'param.mutationStrength'],
  },
  'param.mutationStrength': {
    group: 'Ewolucja',
    title: 'Siła mutacji wag',
    short: 'Typowa wielkość zmiany wagi sieci przy mutacji (odchylenie standardowe).',
    more: ['Małe kroki to „dostrajanie” mózgu; duże to skoki, które częściej niszczą, ale czasem otwierają zupełnie nową strategię.'],
    see: ['mutation', 'neuron'],
  },
  'param.traitMutationStrength': {
    group: 'Ewolucja',
    title: 'Siła mutacji cech',
    short: 'Typowa wielkość zmiany cechy fizycznej (w skali 0–1) przy mutacji.',
    see: ['mutation', 'genome'],
  },

  // ——— klimat ———
  'param.baseTemperature': {
    group: 'Świat i środowisko',
    title: 'Temperatura bazowa',
    short: '1 = komfort. Wartości niższe oznaczają stałe zimno: wyższe koszty metabolizmu i utrata zdrowia dla organizmów bez izolacji.',
    try: 'Ustaw 0,6 i obserwuj, czy odporność na zimno rośnie w populacji.',
    see: ['temperature', 'trait.coldTolerance'],
  },
  'param.coldMetabK': {
    group: 'Świat i środowisko',
    title: 'Wpływ zimna na metabolizm',
    short: 'O ile rosną koszty życia na każdą jednostkę obciążenia zimnem: mnożnik 1 + K · obciążenie.',
    see: ['temperature'],
  },
  'param.coldDamage': {
    group: 'Świat i środowisko',
    title: 'Obrażenia od zimna',
    short: 'Utrata zdrowia na tick, proporcjonalna do kwadratu obciążenia zimnem.',
    see: ['temperature', 'health'],
  },
  'param.seasonPeriod': {
    group: 'Świat i środowisko',
    title: 'Okres pór roku',
    short: 'Długość pełnego cyklu zima–lato w tickach. 0 = brak pór roku.',
    see: ['seasons'],
  },
  'param.seasonTempAmp': {
    group: 'Świat i środowisko',
    title: 'Amplituda temperatury sezonowej',
    short: 'O ile spada temperatura w środku zimy.',
    see: ['seasons', 'temperature'],
  },
  'param.seasonGrowthAmp': {
    group: 'Świat i środowisko',
    title: 'Amplituda wzrostu sezonowego',
    short: 'O ile spada tempo wzrostu zasobów w środku zimy (0,5 = o połowę).',
    see: ['seasons'],
  },

  // ——— cechy genomu ———
  'trait.size': {
    group: 'Organizm',
    title: 'Gen: rozmiar',
    short: 'Promień ciała od 2 do 6. Większe ciało ma większy zapas energii, większy kęs, dłuższe życie i przewagę w walce — ale droższe życie i droższe potomstwo.',
    more: ['Rozmiar jest tym genem, który najmocniej „czuje” środowisko: bez drapieżników małe ciała zwykle wygrywają, z drapieżnikami wyższe wartości utrzymują się dłużej.'],
    fun: 'Zasada „mniejsze rozmnażają się szybciej, większe żyją dłużej” (strategie r i K) opisuje wiele grup zwierząt, od bakterii po słonie.',
    see: ['allometry', 'kleiber', 'tradeoff'],
  },
  'trait.speed': {
    group: 'Organizm',
    title: 'Gen: prędkość maksymalna',
    short: 'Górna granica prędkości od 0,5 do 3 jednostek na tick. Faktyczną prędkość wybiera sieć (wyjście „ciąg”).',
    more: ['Sam gen nie oznacza, że organizm biega: koszt zależy od prędkości do kwadratu, więc sieć może pozostać powolna mimo wysokiego limitu.'],
    see: ['param.moveCoef', 'outputs'],
  },
  'trait.sense': {
    group: 'Organizm',
    title: 'Gen: zasięg zmysłów',
    short: 'Jak daleko organizm „widzi” pokarm i sąsiadów: od 12 do 80 jednostek. Koszt rośnie z kwadratem zasięgu.',
    more: ['Nawet najmniejszy zasięg wystarcza do lokalnej taksji (podążania po gradiencie pokarmu), więc selekcja często obniża ten gen. Daleki wzrok ma sens tam, gdzie pokarm jest rozproszony lub miejsca się zmieniają.'],
    see: ['param.senseCoef', 'patches'],
  },
  'trait.reproThreshold': {
    group: 'Organizm',
    title: 'Gen: próg rozrodu',
    short: 'Jaki ułamek zapasów energii (45–95%) organizm musi zgromadzić, zanim się rozmnoży.',
    more: ['Wysoki próg = późniejszy, lepiej zaopatrzony rodzic. Niski = szybkie rozmnażanie przy mniejszym zapasie. Zależy to od tego, jak ryzykowne jest życie.'],
    see: ['reproduction'],
  },
  'trait.investment': {
    group: 'Organizm',
    title: 'Gen: inwestycja w potomka',
    short: 'Jaką część swojej pojemności energii (12–45%) rodzic przekazuje dziecku. Większa inwestycja = silniejszy, ale rzadszy potomek.',
    more: ['To klasyczny kompromis jakość kontra liczba: mało dobrze wyposażonych dzieci czy dużo słabych.'],
    see: ['reproduction', 'tradeoff'],
  },
  'trait.maturity': {
    group: 'Organizm',
    title: 'Gen: wiek dojrzałości',
    short: 'Po ilu tickach (80–450) organizm może się pierwszy raz rozmnożyć.',
    more: ['Wcześniejsze dojrzewanie skraca pokolenie, ale odbiera czas na zgromadzenie zapasów.'],
    see: ['reproduction', 'param.accidentRate'],
  },
  'trait.coldTolerance': {
    group: 'Organizm',
    title: 'Gen: odporność na zimno',
    short: 'Ile deficytu temperatury organizm znosi bez kar (do 0,6). Odporność kosztuje stale — również w ciepłym świecie.',
    more: ['W świecie bez zimna gen ten jest tylko kosztem, więc zwykle maleje (albo dryfuje, jeśli jest sprzężony z użytecznym mózgiem — to hitchhiking).'],
    see: ['temperature', 'param.coldUpkeepCoef', 'hitchhiking'],
  },
  'trait.bite': {
    group: 'Organizm',
    title: 'Gen: wielkość kęsa',
    short: 'Ile pokarmu organizm może zjeść na tick. Większy kęs oznacza szybsze jedzenie, ale gorsze trawienie (od 95% do 50% sprawności).',
    more: ['W obfitym świecie liczy się szybkość jedzenia, w ubogim — sprawność trawienia. To kolejny kompromis bez uniwersalnie najlepszej wartości.'],
    see: ['tradeoff', 'param.eatCoef'],
  },
  'trait.hue': {
    group: 'Organizm',
    title: 'Gen: znacznik linii (neutralny)',
    short: 'Nie wpływa na przeżycie ani rozmnażanie. Służy wyłącznie do śledzenia pokrewieństwa kolorem na planszy.',
    more: ['Ponieważ hue jest dziedziczony i mutuje, potomkowie są podobnego koloru. Gen neutralny to też dobry „zegar” dryfu: zmienia się wyłącznie przez przypadek.'],
    fun: 'W biologii takie neutralne znaczniki (np. mikrosatelity) pozwalają badać pokrewieństwo i historię populacji.',
    see: ['drift', 'clade'],
  },
  'trait.memory': {
    group: 'Mózg',
    title: 'Gen: trwałość pamięci',
    short: 'Jak szybko zanika stan pamięci rekurencyjnej: od λ = 0,5 (niemal natychmiast) do 0,99 (połowa po ok. 69 tickach).',
    more: ['Długa pamięć pomaga w szukaniu zmieniających się plam, ale „zatruwa” decyzje, gdy sytuacja szybko się zmienia.'],
    see: ['memory'],
  },
  'trait.diet': {
    group: 'Ekologia',
    title: 'Gen: dieta',
    short: '0 = czysty roślinożerca, 1 = drapieżnik. Powyżej 0,02 organizm może atakować; rośliny trawi gorzej (×(1−0,9·dieta)), a mięso w stopniu 85%·dieta.',
    more: ['Dieta to ciągły kompromis, nie przełącznik: wszystkożercy łączą oba sposoby żywienia, ale żaden nie jest tak wydajny jak wyspecjalizowany.'],
    fun: 'Zasada „energia traci się na każdym szczeblu” (ok. 10% przechodzi dalej) jest w ekologii nazywana regułą Lindemana. W GENESIS wydajność jest wyższa, ale nadal poniżej 100%.',
    see: ['predation', 'herbivore', 'trophic'],
  },
};
